import { Component, Input, OnInit, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { AuctionStatus, PaymentStatus } from 'src/app/API.service';
import { AuctionModelService, AuctionReportLineItem } from 'src/app/model/auction.model';
import moment from 'moment';
import { environment } from '../../../environments/environment';
import { InvoiceModelService } from 'src/app/model/invoice.model';
import { NbDialogService } from '@nebular/theme';
import { AuctionReportUpdateWinningBuyerDialogComponent } from '../auction-report-update-winning-buyer-dialog/auction-report-update-winning-buyer-dialog.component';

interface AuctionReportGroupListing {
  [sellerKey: string]: {
    name: string;
    bidders: AuctionReportBidder
  }
}

interface AuctionReportBidder {
  [bidderKey: string]: {
    name: string;
    paymentStatus: PaymentStatus;
    invoiceDateSent: string;
    lineItems: AuctionReportLineItem[];
  }
}

interface AuctionReportMessageEvent {
  message: 'updatePaymentStatus' | 'splitCompleted' | 'adjustmentCompleted' | 'saleStatusChanged';
  lot: AuctionReportLineItem;
}

interface PaymentStatusOption {
  key: PaymentStatus;
  value: string;
}
@Component({
  selector: 'app-auction-report-sold-lot-group',
  templateUrl: './auction-report-sold-lot-group.component.html',
  styleUrls: ['./auction-report-sold-lot-group.component.scss']
})
export class AuctionReportSoldLotGroupComponent implements OnInit {

  @Input() soldLineItems: AuctionReportLineItem[];
  @Output() messageEvent = new EventEmitter<AuctionReportMessageEvent>();

  public groupListing: AuctionReportGroupListing = {};
  public invoicesSent: boolean = false;
  public downloadingAllInvoices: boolean = false;
  public downloadingSellerReports: boolean = false;
  public sellerReportErrors: string[] = [];
  public sellerReportLinks: { businessAccountId: string; sellerName: string; downloadUrl: string }[] = [];

  public paymentStatusOptions: PaymentStatusOption[] = [
    { key: PaymentStatus.UNPAID, value: 'Unpaid' },
    { key: PaymentStatus.DIRECT_PAID, value: 'Direct-Paid' },
    { key: PaymentStatus.PAID_SALE_REP, value: 'Paid Sale Rep' },
    { key: PaymentStatus.UNCOLLECTIBLE, value: 'Uncollectible' },
  ];

  public loading: { [combinedSellerBuyerKey: string]: boolean } = {};
  public downloadingInvoice: { [combinedSellerBuyerKey: string]: boolean } = {};
  public checkNumbers: { [invoiceId: string]: string } = {};
  public savedCheckNumbers: { [invoiceId: string]: string } = {};

  constructor(
    public auctionModel: AuctionModelService,
    public invoiceModel: InvoiceModelService,
    private dialogService: NbDialogService) { }

  ngOnInit(): void {
    this.groupListing = this.groupAuctionReportBySellerAndBidder();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.soldLineItems && changes.soldLineItems.currentValue !== changes.soldLineItems.previousValue) {
      this.groupListing = this.groupAuctionReportBySellerAndBidder();

      for (let sellerKey of this.sellerKeys()) {
        for (let buyerKey of this.buyerKeys(sellerKey)) {
          if (!this.invoiceNotCreatedYet(sellerKey, buyerKey)) {
            this.invoicesSent = true;
            break;
          }
        }
      }
    }
  }

  public sellerKeys() {
    return Object.keys(this.groupListing);
  }

  public buyerKeys(sellerKey: string): string[] {
    if (!this.groupListing.hasOwnProperty(sellerKey)) return [];

    return Object.keys(this.groupListing[sellerKey].bidders);
  }

  public groupAuctionReportBySellerAndBidder(): AuctionReportGroupListing {
    var result = {} as AuctionReportGroupListing;

    this.soldLineItems.forEach(item => {
      const sellerKey = item.sellerEmail || "Unknown";
      const bidderKey = item.winningBidderEmail || "Unknown";
      if (!result.hasOwnProperty(sellerKey)) {
        result[sellerKey] = {
          name: item.sellerName,
          bidders: {} as AuctionReportBidder
        }
      }
      const bidders = result[sellerKey].bidders;
      if (!bidders.hasOwnProperty(bidderKey)) {
        bidders[bidderKey] = {
          name: `${item.winningBidderFirstName ? item.winningBidderFirstName : ""} ${item.winningBidderLastName ? item.winningBidderLastName : ""}`,
          paymentStatus: item.paymentStatus,
          invoiceDateSent: item.invoice && item.invoice.invoiceSentDate || null,
          lineItems: []
        }
      }
      bidders[bidderKey].lineItems.push(item);

      // Seed once per invoiceId from the fetched invoice, then leave it alone: this method
      // re-runs on unrelated updates (e.g. editWinningBuyer's dialog close) against the same
      // soldLineItems reference, and re-seeding every time would revert an in-flight edit or a
      // just-saved value back to whatever was loaded at the start of this component's lifecycle.
      if (item.invoiceId && !(item.invoiceId in this.savedCheckNumbers)) {
        const checkNumber = (item.invoice && item.invoice.checkNumber) || '';
        this.checkNumbers[item.invoiceId] = checkNumber;
        this.savedCheckNumbers[item.invoiceId] = checkNumber;
      }
    });

    return result;
  }

  public async onUpdatePaymentStatus(sellerKey: string, buyerKey: string) {
    const itemsToUpdate = this.groupListing[sellerKey].bidders[buyerKey].lineItems;
    const that = this;

    const updatePaymentStatuses = async() => {
      const promises = itemsToUpdate.map(async item => {
        let result = {};
        const actualLineItem = that.auctionModel.activeAuctionReport.lineItems.find(x => x.lotId === item.lotId);
        if (actualLineItem) {
          actualLineItem.paymentStatus = this.groupListing[sellerKey].bidders[buyerKey].paymentStatus;
          result = await that.auctionModel.activeAuctionReport.updatePaymentStatus(actualLineItem);
        }
        return result;
      })
      await Promise.all(promises);
    }
    updatePaymentStatuses();
  }

  public async onSaveCheckNumber(invoiceId: string) {
    if (!invoiceId) {
      return;
    }
    const value = this.checkNumbers[invoiceId] || '';
    if (value === (this.savedCheckNumbers[invoiceId] || '')) {
      // Nothing changed since the last load/save - avoid an unnecessary write
      // and never null out a previously-saved value on an incidental blur.
      return;
    }
    await this.invoiceModel.updateInvoiceCheckNumber(invoiceId, value);
    this.savedCheckNumbers[invoiceId] = value;
  }

  public async onSendInvoice(sellerKey: string, buyerKey: string) {
    this.loading[sellerKey + buyerKey] = true;
    const result = await this.auctionModel.activeAuctionReport.sendInvoice(sellerKey, buyerKey);
    const invoice = result.invoice;
    const itemsToUpdate = this.groupListing[sellerKey].bidders[buyerKey].lineItems;

    itemsToUpdate.forEach(item => {
      const lineItem = this.auctionModel.activeAuctionReport.lineItems.find(x => x.lotId === item.lotId);
      if (lineItem) {
        lineItem.invoice = invoice;
        lineItem.invoiceId = invoice.invoiceId;
      }
    });
    this.loading[sellerKey + buyerKey] = false;
    this.invoicesSent = true;
  }

  public formatAsMonthYear(dateString: string): string {
    const momentDate = moment(dateString);
    if (!momentDate.isValid()) return dateString;
    return momentDate.format("MM/DD hh:mma");
  }

  public isLoading(sellerKey: string, buyerKey: string): boolean {
    return this.loading[buyerKey + sellerKey] || false;
  }

  public loadingText(text: string, buyerKey: string, sellerKey: string) {
    return this.isLoading(sellerKey, buyerKey) ? "" : text;
  }

  public onViewInvoice(invoiceId: string) {
    window.open(
      `https://${environment.buyer_portal_path_url}/invoice/${invoiceId}`,
      '_blank'
    );
  }

  public invoiceNotCreatedYet(sellerKey: string, buyerKey: string) {
    return this.groupListing[sellerKey].bidders[buyerKey].lineItems.length > 0 && !this.groupListing[sellerKey].bidders[buyerKey].lineItems[0].invoiceId;
  }

  public invoiceNotAutomatedPaidYet(sellerKey: string, buyerKey: string) {
    return this.groupListing[sellerKey].bidders[buyerKey].paymentStatus != PaymentStatus.AUTOMATED_PAID;
  }

  public isAuctionSettled() {
    return this.auctionModel.activeAuction.status === AuctionStatus.SETTLED;
  }

  public sendInvoiceEnabled(sellerKey: string, buyerKey: string) {
    return this.invoiceNotCreatedYet(sellerKey, buyerKey) && !this.isAuctionSettled();
  }

  public getAddress(physicalAddress) {
    var returnValue = "";
    if(physicalAddress != undefined) {
      for(let address of physicalAddress) {
        returnValue = returnValue + address + " ";
      }
      if(returnValue.replace(/ /g,'').length == 0) {
        returnValue = "";
      }
    }
    return returnValue;
  }

  public async downloadAllInvoices() {
    this.downloadingAllInvoices = true;
    const result = await this.invoiceModel.downloadInvoices(this.auctionModel.activeAuction.auctionId);
    this.triggerDownload(result.downloadUrl);
    this.downloadingAllInvoices = false;
  }

  public uniqueSellerBusinessAccounts(): { businessAccountId: string; sellerName: string }[] {
    const byId: { [id: string]: string } = {};
    (this.soldLineItems || []).forEach(item => {
      if (!item.businessAccountId) {
        return;
      }
      byId[item.businessAccountId] = item.sellerName || item.businessAccountId;
    });
    return Object.keys(byId).map(businessAccountId => ({
      businessAccountId,
      sellerName: byId[businessAccountId]
    }));
  }

  public async generateSellerReports() {
    this.downloadingSellerReports = true;
    this.sellerReportErrors = [];
    this.sellerReportLinks = [];
    const auctionId = this.auctionModel.activeAuction.auctionId;
    const sellers = this.uniqueSellerBusinessAccounts();

    for (const seller of sellers) {
      try {
        const result = await this.invoiceModel.downloadSellerReport(auctionId, seller.businessAccountId);
        if (result && result.status === 'SUCCESS' && result.downloadUrl) {
          this.sellerReportLinks.push({
            businessAccountId: seller.businessAccountId,
            sellerName: seller.sellerName,
            downloadUrl: result.downloadUrl
          });
          this.triggerDownload(result.downloadUrl);
        } else {
          this.sellerReportErrors.push(
            `${seller.sellerName}: ${(result && result.message) || 'Download failed'}`
          );
        }
      } catch (e) {
        console.error('Error downloading seller report', e);
        this.sellerReportErrors.push(`${seller.sellerName}: Error downloading seller report`);
      }
    }

    this.downloadingSellerReports = false;
  }

  public async downloadInvoice(invoiceId: string, sellerKey: string, buyerKey: string) {
    try {
      this.downloadingInvoice[sellerKey + buyerKey] = true;
      const result = await this.invoiceModel.downloadInvoice(invoiceId);
      this.triggerDownload(result.downloadUrl);
    } catch (e) {
      console.error("Error downloading invoice", e);
    } finally {
      this.downloadingInvoice[sellerKey + buyerKey] = false;
    }
  }

  public triggerDownload(downloadUrl: string) {
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = downloadUrl;
    const components = downloadUrl.split("/");
    a.download = components[components.length - 1];
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
  }

  public receiveMessage($event: AuctionReportMessageEvent): void {
    // Forward the message event to the parent component
    this.messageEvent.emit($event);
  }

  /**
   * Calculates the gross sale total for a specific buyer.
   * This is the sum of every "Final Price" lot purchased by that buyer in this sale, no deductions.
   *
   * Note: The calculation preserves decimal precision (e.g., 1000.50 + 2000.75 = 3001.25).
   * The return value is a number that may include decimal places.
   */
  public getBuyerGrossTotal(sellerKey: string, buyerKey: string): number {
    if (!this.groupListing[sellerKey] || !this.groupListing[sellerKey].bidders[buyerKey]) {
      return 0;
    }

    const lineItems = this.groupListing[sellerKey].bidders[buyerKey].lineItems;
    return lineItems.reduce((total, lot) => {
      const finalPrice = lot.finalPrice;
      // Handle null, undefined, or NaN values
      if (finalPrice == null || isNaN(finalPrice)) {
        return total;
      }
      // Addition preserves decimal precision - no integer conversion
      return total + finalPrice;
    }, 0);
  }

  /**
   * Formats the buyer gross total as currency with dollar sign, comma separators, and two decimal places.
   */
  public getFormattedBuyerGrossTotal(sellerKey: string, buyerKey: string): string {
    const total = this.getBuyerGrossTotal(sellerKey, buyerKey);
    return `$${total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  /**
   * Opens the Edit Winning Buyer dialog for the buyer/seller group.
   * Uses the first line item from the buyer group to populate the form.
   */
  public editWinningBuyer(sellerKey: string, buyerKey: string): void {
    const lineItems = this.groupListing[sellerKey].bidders[buyerKey].lineItems;
    if (lineItems.length === 0) {
      return;
    }

    // Use the first line item to populate the dialog
    const firstLineItem = lineItems[0];

    this.dialogService.open(AuctionReportUpdateWinningBuyerDialogComponent, {
      hasScroll: false,
      closeOnBackdropClick: true,
      closeOnEsc: true,
      context: {
        lineItem: firstLineItem,
        groupLevel: true, // Flag to indicate this is at the group level
        sellerEmail: sellerKey,
        winningBidderEmail: buyerKey
      },
      dialogClass: 'modal-medium'
    }).onClose.subscribe((result) => {
      if (result && result.updatedLineItems) {
        // Refresh the group listing to reflect the updated line items
        this.groupListing = this.groupAuctionReportBySellerAndBidder();
      }
    });
  }
}

